import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastsComponent } from './core/components/toasts/toasts.component';
import { UserStore } from './core/auth/store/user.store';
import { DictionaryStore } from './features/incidents/store/dictionary.store';
import { PusherService } from './core/notifications/pusher.service';
import { NotificationPreferencesStore } from './features/settings/store/notification-preferences.store';
import { resolveNotificationRoute } from './core/store/notification-inbox.store';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToastsComponent],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnDestroy {
  private userStore = inject(UserStore);
  private dictionaryStore = inject(DictionaryStore);
  private pusherService = inject(PusherService);
  private messageService = inject(MessageService);
  private prefsStore = inject(NotificationPreferencesStore);
  private workflowDictionaryLoaded = false;
  private notificationSub: Subscription | null = null;

  constructor() {
    effect(() => {
      const user = this.userStore.user();
      if (user && !this.workflowDictionaryLoaded) {
        this.workflowDictionaryLoaded = true;
        this.dictionaryStore.loadWorkflowDictionary();
        this.connectPusher(user.email);
      }
    });
  }

  private connectPusher(email: string): void {
    this.pusherService.connect(email);
    this.prefsStore.load();

    this.notificationSub = this.pusherService.notification$.subscribe(event => {
      const pref = this.prefsStore.preferences().find(p => p.event_type === event.event_type);
      const browserOn = pref
        ? (pref.channels.find(c => c.channel === 'browser_push')?.enabled ?? true)
        : true;
      if (!browserOn) return;

      this.messageService.add({
        severity: 'info',
        summary: event.title || 'New Notification',
        detail: event.body || '',
        life: 6000,
        data: resolveNotificationRoute(event.event_type, event.reference_id, {
          ...event.payload,
          branch_number: event.branch_number,
          damage_status: event.damage_status,
        }),
      });
    });
  }

  ngOnDestroy(): void {
    this.notificationSub?.unsubscribe();
    this.pusherService.disconnect();
  }
}
